import { isNative } from "@/lib/platform";

/**
 * Push мэдэгдлийн бүртгэл — `@capacitor-firebase/messaging`.
 *
 * Яагаад `@capacitor/push-notifications` БИШ вэ:
 * тэр plugin нь iOS дээр APNs токен буцаадаг тул backend-ээс Android руу FCM,
 * iOS руу APNs гэж 2 өөр суваг барих шаардлагатай болно. Энэ plugin нь iOS дээр
 * Firebase SDK-аар дамжуулан FCM токен өгдөг тул хоёр платформыг НЭГ сувгаар
 * (FCM) илгээх боломжтой.
 * ⚠ Хоёр plugin зэрэг байвал iOS дээр notification delegate мөргөлдөнө —
 * тиймээс `@capacitor/push-notifications`-ийг устгасан.
 *
 * ⚠ Вэбэд нөлөөлөхгүй:
 *   • Native Firebase SDK нь `ios/` дотор (SPM/CocoaPods) — вэб build хүрдэггүй.
 *   • Plugin-ийг ДИНАМИКААР import хийсэн тул тусдаа chunk болж, вэб хэрэглэгч
 *     татаж авахгүй.
 *   • `firebase` JS SDK (25MB) нь optional peer — СУУЛГААГҮЙ.
 */

let started = false;

/**
 * Push-ийг эхлүүлнэ. Нэвтэрсний дараа нэг удаа дуудна.
 *
 * @param {object}   handlers
 * @param {Function} handlers.onToken       (fcmToken: string) => void — backend-д бүртгэнэ
 * @param {Function} [handlers.onReceived]  апп нээлттэй байхад мэдэгдэл ирэхэд
 * @param {Function} [handlers.onOpened]    хэрэглэгч мэдэгдэл дээр дарахад
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export const initPush = async ({ onToken, onReceived, onOpened } = {}) => {
    if (!isNative()) return { ok: false, reason: "not-native" };
    if (started) return { ok: true, reason: "already-started" };

    let FirebaseMessaging;
    try {
        ({ FirebaseMessaging } = await import("@capacitor-firebase/messaging"));
    } catch (e) {
        return { ok: false, reason: "plugin-missing" };
    }

    try {
        // 1) Зөвшөөрөл (iOS болон Android 13+ дээр заавал)
        let perm = await FirebaseMessaging.checkPermissions();
        if (perm.receive !== "granted") {
            perm = await FirebaseMessaging.requestPermissions();
        }
        if (perm.receive !== "granted") {
            return { ok: false, reason: "permission-denied" };
        }

        // 2) Сонсогчдыг токен авахаас ӨМНӨ холбоно
        if (typeof onReceived === "function") {
            await FirebaseMessaging.addListener("notificationReceived", onReceived);
        }
        if (typeof onOpened === "function") {
            await FirebaseMessaging.addListener("notificationActionPerformed", onOpened);
        }
        // Токен сэргээгдэхэд (OS шинэчилж болно) дахин бүртгүүлнэ
        await FirebaseMessaging.addListener("tokenReceived", (event) => {
            if (event?.token && typeof onToken === "function") onToken(event.token);
        });

        // 3) FCM токен авах
        const { token } = await FirebaseMessaging.getToken();
        if (token && typeof onToken === "function") onToken(token);

        started = true;
        return { ok: true };
    } catch (e) {
        console.error("[push] initPush алдаа", e);
        return { ok: false, reason: "error" };
    }
};

/** Гарах үед токеныг цуцлах (backend-д unregister хийсний дараа дуудна). */
export const deletePushToken = async () => {
    if (!isNative()) return;
    try {
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        await FirebaseMessaging.deleteToken();
    } catch (e) {
        /* plugin байхгүй — алгасна */
    }
};

/** Хүргэгдсэн мэдэгдлүүдийг цэвэрлэнэ (жагсаалт харсны дараа). */
export const clearDeliveredNotifications = async () => {
    if (!isNative()) return;
    try {
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        await FirebaseMessaging.removeAllDeliveredNotifications();
    } catch (e) {
        /* алгасна */
    }
};
